import { z } from "zod/v4";

import { OrganizationDtoSchema } from "../../dtos/organizations";
import { UrlMatch, UrlMatchList } from "../../dtos/url-match";
import { AnalyzeResourceResponseBodyType } from "../../functions/analytics";
import { castToNumber, castToString } from "../../utils";

export const ConversionStageReachedList = [
  "started",
  "contact_info_submitted",
  "address_info_submitted",
  "any",
  "added_to_cart",
] as const;
export type ConversionStageReachedType =
  (typeof ConversionStageReachedList)[number];
export const DeviceTypeList = ["any", "mobile", "desktop"] as const;
export type DeviceType = (typeof DeviceTypeList)[number];

export const AnalyticsResourceListSchema = [
  "campaigns",
  "experiences",
  "sitewide",
] as const;
export type AnalyticsResourceType =
  (typeof AnalyticsResourceListSchema)[number];

export const VisitorTypeSchema = ["any", "new", "returning"] as const;
export type VisitorType = (typeof VisitorTypeSchema)[number];

export const AnalyzeContext = z.object({
  org: OrganizationDtoSchema.pick({ id: true, shopId: true, timezone: true }),
  latestRunTs: z.string(),
  cacheInterval: z.number(),
});

export type AnalyzeContextType = z.infer<typeof AnalyzeContext>;

export const ChannelTypeSchema = [
  "Referral",
  "All Social",
  "Organic Social",
  "Paid Social",
  "Organic Search",
  "Paid Search",
  "Email",
  "Google",
  "Facebook",
  "Instagram",
  "Linktree",
  "TikTok",
  "Twitter",
  "Direct",
  "SMS",
  "Other",
  "Meta",
  "Link Tree",
  "Wunderkind Text",
  "Youtube",
  "any",
] as const;
export type ChannelType = (typeof ChannelTypeSchema)[number];

export const UrlParamPayloadSchema = z.object({
  queryParam: z.string().nullable(),
  queryParamValue: z.string().nullable(),
  queryParamFilterType: z.enum(UrlMatchList).nullable(),
});

export const LandingPageFilterList = [
  "equals",
  "contains",
  "startsWith",
  "endsWith",
  "doesNotEqual",
  "doesNotContain",
  "doesNotStartWith",
  "doesNotEndWith",
  "isNull",
  "isNotNull",
] as const satisfies readonly UrlMatch[];

export type LandingPageFilterType = (typeof LandingPageFilterList)[number];

export const LandingPagePayloadSchema = z.object({
  landingPageUrlPath: z.string().nullable(),
  landingPageUrlPathFilterType: z.enum(LandingPageFilterList).nullable(),
});

export type UrlParamPayloadType = z.infer<typeof UrlParamPayloadSchema>;

export const FileExportIdList = [
  "ordersExport",
  "lineItemsExport",
  "ordersReconciliationExport",
] as const;

export type FileExportId = (typeof FileExportIdList)[number];

export const DatasetIdList = [
  "ordersExport",
  "lineItemsExport",
  "ordersReconciliation",
  "ordersReconciliationExport",
  "ordersReconciliationReasons",
  "aiProfit",
  "variation_overview",
  "variation_audience",
  "variation_timeseries_discrete",
  "variation_timeseries_cumulative",
  "experience_countries",
  "variation_shipping_method",
  "variation_histogram",
  "variation_products",
  "variation_product_groups",
  "variation_order_unit_mix",
  "custom_events_plugin",
  "custom_events_server",
  "order_reconciliation",
  "order_reconciliation_export",
  "profit",
  "country_codes",
] as const;

export type DatasetId = (typeof DatasetIdList)[number];

export const BaseAnalyticsPayloadSchema = z.object({
  datasetId: z.enum(DatasetIdList),
  startTime: castToString.or(z.number()).nullable(),
  endTime: castToString.or(z.number()).nullable(),
  shopifyDomain: z.string(),
  latestRunTs: z.string(),
  timezone: z.string(),
  urlParam: UrlParamPayloadSchema,
  landingPage: LandingPagePayloadSchema.nullish(),
  deviceType: z.enum(DeviceTypeList).nullable(),
  visitorType: z.enum(VisitorTypeSchema).nullable(),
  channels: z.enum(ChannelTypeSchema).array(),
  shippingExpensePerOrder: castToNumber.or(z.string()).nullable(),
  transactionFeePct: castToNumber.or(z.string()).nullable(),
  resourceType: z.enum(AnalyticsResourceListSchema),
  countryCodes: z.string().array(),
});

export type CastedTimes = {
  startTime: string | number | undefined | null;
  endTime: string | number | undefined | null;
};

export type CastedTimesTinybird = {
  startTs: string | number | undefined | null;
  endTs: string | number | undefined | null;
};

export const AnalyticsViewTypeList = [
  "test_orders_only",
  "sitewide_orders",
  "none",
] as const;
export type AnalyticsViewType = (typeof AnalyticsViewTypeList)[number];

export const StatSigViewSchema = ["best", "beatControl"] as const;
export type StatSigViewType = (typeof StatSigViewSchema)[number];

export const UserBehaviorFilterSchema = z.object({
  viewedCheckout: z.boolean().nullish(),
  addedToCart: z.boolean().nullish(),
  viewedProductPage: z.boolean().nullish().optional(),
  viewedCollectionPage: z.boolean().nullish().optional(),
});
export type UserBehaviorFilterType = z.infer<typeof UserBehaviorFilterSchema>;

export type AnalyticsResponsePayload = {
  metrics: AnalyzeResourceResponseBodyType["metrics"];
  loss: AnalyzeResourceResponseBodyType["loss"];
};

export const HistogramMetricList = [
  "net_revenue",
  "net_product_revenue",
  "total_discount",
  "unit_quantity",
] as const;
export type HistogramMetricType = (typeof HistogramMetricList)[number];

export const HistogramKindList = ["histogram", "kde", "kde_cdf"] as const;
export type HistogramKindType = (typeof HistogramKindList)[number];

export const CustomEventsFilterTypeList = ["any", "all", "none"] as const;
export type CustomEventsFilterType =
  (typeof CustomEventsFilterTypeList)[number];

export const AnalyticsResourceTypeV2List = [
  "sitewide",
  "exports",
  "experiences",
  "customEvents",
  "summaryMetrics",
] as const;
export type AnalyticsResourceV2Type =
  (typeof AnalyticsResourceTypeV2List)[number];
